import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStore } from '../../context/StoreContext';
import Header from './Header';
import {
  FiDollarSign, FiSettings, FiShield, FiTerminal, FiGrid, FiBox, FiShoppingCart, FiUsers, FiLogOut, FiRepeat,
  FiTag, FiList, FiHome, FiChevronsRight, FiTruck, FiBookOpen, FiKey, FiGlobe, FiShare2, FiCode, FiMenu, FiX,
  FiClipboard, FiUserCheck, FiLayers, FiBarChart2, FiArchive, FiDatabase, FiFileText, FiUpload, FiSmartphone
} from 'react-icons/fi';
import { isCashierUser } from '../../utils/roleUtils';
import { isWebApp } from '../../utils/platform';
import { getCurrentAppProfile, getNavModulesForProfile, NAV_GROUP_ORDER } from '../../config/moduleRegistry';

const SidebarLink = ({ to, icon, children, isCollapsed, onClick }) => (
  <NavLink
    to={to}
    end
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center px-4 py-3 text-gray-200 hover:bg-gray-700 rounded-md transition-colors ${
        isActive ? 'bg-gray-900 font-semibold' : ''
      }`
    }
  >
    {icon}
    {!isCollapsed && <span className="ml-3">{children}</span>}
  </NavLink>
);

// --- NEW COMPONENT for sub-headings ---
const SidebarSubheading = ({ children, isCollapsed }) => (
    !isCollapsed && <p className="px-4 pt-4 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">{children}</p>
);

const ICON_MAP = {
  FiDollarSign: FiDollarSign,
  FiSettings: FiSettings,
  FiShield: FiShield,
  FiTerminal: FiTerminal,
  FiGrid: FiGrid,
  FiBox: FiBox,
  FiShoppingCart: FiShoppingCart,
  FiUsers: FiUsers,
  FiRepeat: FiRepeat,
  FiTag: FiTag,
  FiList: FiList,
  FiHome: FiHome,
  FiTruck: FiTruck,
  FiBookOpen: FiBookOpen,
  FiKey: FiKey,
  FiGlobe: FiGlobe,
  FiShare2: FiShare2,
  FiCode: FiCode,
  FiClipboard: FiClipboard,
  FiUserCheck: FiUserCheck,
  FiLayers: FiLayers,
  FiBarChart2: FiBarChart2,
  FiArchive: FiArchive,
  FiDatabase: FiDatabase,
  FiFileText: FiFileText,
  FiUpload: FiUpload,
  FiSmartphone: FiSmartphone,
};

const MainLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const { activeStore, userStores, switchStore, isInitialAdmin } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const appProfile = useMemo(() => getCurrentAppProfile(), []);
  const isWebClient = useMemo(() => isWebApp(), []);

  const hasPermission = useCallback(
    (permission) => user?.permissions?.includes(permission),
    [user?.permissions]
  );
  const isCashier = useMemo(() => isCashierUser(user), [user]);

  const visibleNavModules = useMemo(() => {
    const modules = getNavModulesForProfile(appProfile);
    return modules.filter((module) => {
      if (!module?.nav || !module.path) return false;
      if (module.webOnly && !isWebClient) return false;
      if (module.hideForCashier && isCashier) return false;
      if (typeof module.visibleWhen === 'function') {
        const isVisible = module.visibleWhen({ user, isCashier, isWebApp: isWebClient });
        if (!isVisible) return false;
      }
      const requiredPermissions = Array.isArray(module.requiredPermissions)
        ? module.requiredPermissions.filter((permission) => typeof permission === 'string' && permission.trim().length > 0)
        : [];
      if (!requiredPermissions.length) return true;
      if (module.requireAll === false) {
        return requiredPermissions.some((permission) => hasPermission(permission));
      }
      return requiredPermissions.every((permission) => hasPermission(permission));
    });
  }, [appProfile, hasPermission, isCashier, isWebClient, user]);

  const groupedNavModules = useMemo(() => {
    const grouped = new Map();
    visibleNavModules
      .slice()
      .sort((a, b) => (a.nav?.order || 0) - (b.nav?.order || 0))
      .forEach((module) => {
        const group = module.nav?.group || 'General';
        const modules = grouped.get(group) || [];
        modules.push(module);
        grouped.set(group, modules);
      });
    return grouped;
  }, [visibleNavModules]);

  const orderedVisibleGroups = useMemo(
    () => NAV_GROUP_ORDER.filter((group) => (groupedNavModules.get(group) || []).length > 0),
    [groupedNavModules]
  );

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchStore = () => {
    if (window.confirm('Are you sure you want to switch stores? Any unsaved progress, like a current sale, will be lost.')) {
      switchStore();
    }
  };

  const renderSidebarContent = (mobile = false) => (
    <>
      <div className="flex items-center justify-between h-16 bg-gray-900 px-4">
        {(mobile || !isCollapsed) && <span className="text-white font-bold uppercase text-lg truncate">{process.env.REACT_APP_TITLE || 'POS System'}</span>}
        <button type="button" onClick={() => mobile ? setIsMobileMenuOpen(false) : setIsCollapsed(!isCollapsed)} className="min-h-11 min-w-11 flex items-center justify-center text-gray-400 hover:text-white" aria-label={mobile ? 'Close navigation' : isCollapsed ? 'Expand navigation' : 'Collapse navigation'}>
          {mobile || !isCollapsed ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </div>
      <div className="p-4 border-b border-t border-gray-700">
        {(mobile || !isCollapsed) && <p className="text-xs text-gray-400">Current Store</p>}
        <div className="flex justify-between items-center">
          {(mobile || !isCollapsed) && <p className="text-white font-semibold truncate">{activeStore.name}</p>}
          {userStores.length > 1 && (
            <button type="button" onClick={handleSwitchStore} className="min-h-11 min-w-11 flex items-center justify-center text-gray-400 hover:text-white" title="Switch Store" aria-label="Switch Store">
              <FiChevronsRight />
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-col flex-1 overflow-y-auto p-4">
        <nav className="flex-1 space-y-1" aria-label={mobile ? 'Mobile navigation' : 'Main navigation'}>
          {orderedVisibleGroups.map((group, groupIndex) => {
            const modules = groupedNavModules.get(group) || [];
            return (
              <React.Fragment key={group}>
                {groupIndex > 0 && <hr className="my-3 border-gray-700" />}
                {group !== 'General' && <SidebarSubheading isCollapsed={!mobile && isCollapsed}>{group}</SidebarSubheading>}
                {modules.map((module) => {
                  const IconComponent = ICON_MAP[module.nav?.icon] || FiGrid;
                  return (
                    <SidebarLink key={module.id} to={module.path} icon={<IconComponent />} isCollapsed={!mobile && isCollapsed} onClick={mobile ? () => setIsMobileMenuOpen(false) : undefined}>
                      {module.nav?.label || module.title}
                    </SidebarLink>
                  );
                })}
              </React.Fragment>
            );
          })}
        </nav>
        <div className="mt-auto">
          <button type="button" onClick={handleLogout} className="flex min-h-11 items-center w-full px-4 py-3 text-gray-200 hover:bg-gray-700 rounded-md transition-colors">
            <FiLogOut />
            {(mobile || !isCollapsed) && <span className="ml-3">Logout</span>}
          </button>
        </div>
      </div>
    </>
  );

  if (!activeStore) {
    return null;
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`hidden md:flex flex-col ${isCollapsed ? 'w-16' : 'w-64'} bg-gray-800 transition-all duration-300`}>
        {renderSidebarContent()}
      </div>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button type="button" className="absolute inset-0 w-full bg-black/50" aria-label="Close navigation" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="relative flex h-full w-[min(20rem,85vw)] flex-col bg-gray-800 shadow-2xl">
            {renderSidebarContent(true)}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title={title} onOpenMenu={() => setIsMobileMenuOpen(true)} />
        {isInitialAdmin && (
          <div className="bg-yellow-400 text-center p-2 font-semibold text-yellow-900 shadow-md">
            System Setup Mode: Please create your first store and then assign it to your user account.
          </div>
        )}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
